import React, { useState } from "react";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
  DragIndicator,
  EditOutlined,
  ErrorOutline,
  PersonOutline,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import WorkflowCanvasIcon from "./WorkflowCanvasIcon";
import {
  describeWorkflowPerson,
  describeWorkflowRoute,
  WORKFLOW_CANVAS_LABELS,
  WORKFLOW_CANVAS_TYPES,
  WorkflowCanvas,
  WorkflowCanvasType,
} from "./workflowBuilderData";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";
import { useActiveUsers } from "./useActiveUsers";

type MoveDirection = "up" | "down";

interface WorkflowFlowProps {
  canvases: WorkflowCanvas[];
  selectedId: string;
  armedDeleteId: string | null;
  onSelect: (canvasId: string) => void;
  onMove: (canvasId: string, direction: MoveDirection) => void;
  onDelete: (canvasId: string) => void;
  onArmDelete: (canvasId: string) => void;
  onInsert: (type: WorkflowCanvasType, afterIndex: number) => void;
  onDropReorder: (sourceId: string, targetId: string) => void;
  /** Validation issues by canvas id, shown on the tile they belong to. Omitted = none shown. */
  issues?: Record<string, string[]>;
}

const WorkflowFlow: React.FC<WorkflowFlowProps> = ({
  canvases,
  selectedId,
  armedDeleteId,
  onSelect,
  onMove,
  onDelete,
  onArmDelete,
  onInsert,
  onDropReorder,
  issues = {},
}) => {
  // View-only tiles still open their (read-only) editor, but carry no grip, tools or "+".
  const viewOnly = useWorkflowReadOnly();
  // The owner chip names a specific user; the list is only fetched when some tile needs it.
  const { users } = useActiveUsers(
    canvases.some((canvas) => canvas.ownerSource === "specific-user" && /^\d+$/.test(canvas.owner)),
  );
  const userNameOf = (userId: string) =>
    users.find((user) => String(user.userId) === userId)?.userName;
  // A route can point at a canvas deleted since: no name means "Not routed", as Validate treats it.
  const canvasNameOf = (routeId: string) => describeWorkflowRoute(routeId, canvases);
  // Nothing may be added after Finalize. A loaded graph can still hold a canvas there (the PIF's
  // "PIF not approved" Cancel), so no connector "+" at or after the first Finalize.
  const finalizeIndex = canvases.findIndex((canvas) => canvas.type === "finalize");

  // The tile is a button, so its own text is flattened under its label; its branch chips and its
  // issues are linked as the description instead, which is what a screen reader announces.
  const describedByFor = (canvas: WorkflowCanvas) =>
    [
      canvas.type === "conditional" && canvas.paths.length > 0 ? `${canvas.id}-branches` : "",
      issues[canvas.id]?.length ? `${canvas.id}-issues` : "",
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  const [insertAnchor, setInsertAnchor] = useState<HTMLElement | null>(null);
  const [insertAfterIndex, setInsertAfterIndex] = useState<number | null>(null);

  const handleTileClick = (event: React.MouseEvent<HTMLElement>) => {
    onSelect(event.currentTarget.dataset.canvasId ?? "");
  };

  const handleTileKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(event.currentTarget.dataset.canvasId ?? "");
    }
  };

  const handleMove = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onMove(
      event.currentTarget.dataset.canvasId ?? "",
      event.currentTarget.dataset.direction as MoveDirection,
    );
  };

  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const canvasId = event.currentTarget.dataset.canvasId ?? "";
    if (armedDeleteId === canvasId) {
      onDelete(canvasId);
    } else {
      onArmDelete(canvasId);
    }
  };

  const handleInsertMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setInsertAnchor(event.currentTarget);
    setInsertAfterIndex(Number(event.currentTarget.dataset.afterIndex));
  };

  const handleInsertMenuClose = () => {
    setInsertAnchor(null);
    setInsertAfterIndex(null);
  };

  const handleInsert = (event: React.MouseEvent<HTMLLIElement>) => {
    if (insertAfterIndex !== null) {
      onInsert(event.currentTarget.dataset.canvasType as WorkflowCanvasType, insertAfterIndex);
    }
    handleInsertMenuClose();
  };

  const handleDragStart = (event: React.DragEvent<HTMLElement>) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", event.currentTarget.dataset.canvasId ?? "");
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData("text/plain");
    const targetId = event.currentTarget.dataset.canvasId ?? "";
    if (sourceId && targetId) onDropReorder(sourceId, targetId);
  };

  return (
    <section className="workflow-builder__canvas-wrap" aria-label="Workflow flow">
      <Box className="workflow-builder__flow">
        {canvases.map((canvas, index) => (
          <React.Fragment key={canvas.id}>
            <Box
              className={`workflow-builder__tile ${
                selectedId === canvas.id ? "workflow-builder__tile--selected" : ""
              } workflow-builder__tile--${canvas.type}${
                issues[canvas.id]?.length ? " workflow-builder__tile--invalid" : ""
              }`}
              component="article"
              data-canvas-id={canvas.id}
              draggable={!viewOnly && canvas.type !== "finalize"}
              role="button"
              tabIndex={0}
              onClick={handleTileClick}
              onKeyDown={handleTileKeyDown}
              onDragStart={viewOnly ? undefined : handleDragStart}
              onDragOver={viewOnly ? undefined : handleDragOver}
              onDrop={viewOnly ? undefined : handleDrop}
              aria-label={`${viewOnly ? "View" : "Edit"} ${canvas.name}`}
              aria-describedby={describedByFor(canvas)}
            >
              {!viewOnly && (
                <DragIndicator className="workflow-builder__drag-handle" aria-hidden="true" />
              )}

              {!viewOnly && (
                <Box className="workflow-builder__tile-tools">
                  <Tooltip title="Edit canvas" placement="top">
                    <IconButton
                      size="small"
                      data-canvas-id={canvas.id}
                      onClick={handleTileClick}
                      aria-label={`Edit ${canvas.name}`}
                    >
                      <EditOutlined />
                    </IconButton>
                  </Tooltip>
                  {/* A disabled button fires no pointer events, so Tooltip needs a live wrapper to
                      listen on — same span trick as FolderList's disabled "Add new" button. */}
                  <Tooltip title="Move up" placement="top">
                    <span className="d-inline-flex">
                      <IconButton
                        size="small"
                        data-canvas-id={canvas.id}
                        data-direction="up"
                        onClick={handleMove}
                        disabled={index === 0 || canvas.type === "finalize"}
                        aria-label={`Move ${canvas.name} up`}
                      >
                        <ArrowUpward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down" placement="top">
                    <span className="d-inline-flex">
                      <IconButton
                        size="small"
                        data-canvas-id={canvas.id}
                        data-direction="down"
                        onClick={handleMove}
                        disabled={
                          index === canvases.length - 1 ||
                          canvas.type === "finalize" ||
                          canvases[index + 1]?.type === "finalize"
                        }
                        aria-label={`Move ${canvas.name} down`}
                      >
                        <ArrowDownward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={armedDeleteId === canvas.id ? "Click again to confirm" : "Delete canvas"}
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      data-canvas-id={canvas.id}
                      // Clicks on any delete button are not "outside": the page disarms on those.
                      data-delete-arm=""
                      onClick={handleDelete}
                      className={
                        armedDeleteId === canvas.id ? "workflow-builder__delete-confirm" : ""
                      }
                      aria-label={
                        armedDeleteId === canvas.id
                          ? `Confirm delete ${canvas.name}`
                          : `Delete ${canvas.name}`
                      }
                    >
                      {armedDeleteId === canvas.id ? (
                        <span>Confirm?</span>
                      ) : (
                        <DeleteOutline />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <Box className="workflow-builder__tile-row">
                <span
                  className={`workflow-builder__canvas-icon workflow-builder__canvas-icon--${canvas.type}`}
                >
                  <WorkflowCanvasIcon type={canvas.type} />
                </span>
                <Box className="workflow-builder__tile-heading">
                  <Typography className="workflow-builder__tile-type">
                    {WORKFLOW_CANVAS_LABELS[canvas.type]}
                  </Typography>
                  <Typography component="h3" className="workflow-builder__tile-name">
                    {canvas.name}
                  </Typography>
                </Box>
              </Box>
              <Typography className="workflow-builder__tile-summary">{canvas.summary}</Typography>
              {canvas.type === "conditional" && canvas.paths.length > 0 && (
                // Branch chips, in evaluation order (first match wins): "path → destination".
                <ul id={`${canvas.id}-branches`} className="workflow-builder__branches">
                  {canvas.paths.map((path, pathIndex) => (
                    <li key={path.id}>
                      <Chip
                        size="small"
                        className={
                          canvasNameOf(path.routeId) ? "" : "workflow-builder__branch--unrouted"
                        }
                        label={`${path.name || `Path ${pathIndex + 1}`} → ${
                          canvasNameOf(path.routeId) ?? "Not routed"
                        }`}
                      />
                    </li>
                  ))}
                </ul>
              )}
              {canvas.ownerSource && (
                <span className="workflow-builder__owner">
                  <PersonOutline />
                  {describeWorkflowPerson(canvas.ownerSource, canvas.owner, userNameOf)}
                </span>
              )}
              {issues[canvas.id]?.length ? (
                <ul id={`${canvas.id}-issues`} className="workflow-builder__tile-issues">
                  {issues[canvas.id].map((message) => (
                    <li key={message}>
                      <ErrorOutline aria-hidden="true" />
                      {message}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Box>

            {index < canvases.length - 1 && (
              <Box className="workflow-builder__connector">
                {!viewOnly && (finalizeIndex < 0 || index < finalizeIndex) && (
                  <Tooltip title="Insert a canvas here" placement="right">
                    <IconButton
                      className="workflow-builder__inject"
                      data-after-index={index}
                      onClick={handleInsertMenuOpen}
                      aria-label={`Insert canvas after ${canvas.name}`}
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>

      <Menu anchorEl={insertAnchor} open={Boolean(insertAnchor)} onClose={handleInsertMenuClose}>
        {WORKFLOW_CANVAS_TYPES.map((canvasType) => (
          <MenuItem
            key={canvasType.type}
            data-canvas-type={canvasType.type}
            onClick={handleInsert}
          >
            <span
              className={`workflow-builder__menu-icon workflow-builder__canvas-icon--${canvasType.type}`}
            >
              <WorkflowCanvasIcon type={canvasType.type} />
            </span>
            {canvasType.label}
          </MenuItem>
        ))}
      </Menu>
    </section>
  );
};

export default WorkflowFlow;
